import { FC } from 'react';

const Card: FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div
    className={`overflow-hidden rounded-2xl bg-zinc-800/80 ring-1 ring-white/15 shadow-sm dark:bg-white dark:ring-zinc-200 ${className}`}
  >
    {children}
  </div>
);

const CardBody: FC<{ children: React.ReactNode; padded?: boolean; className?: string }> = ({ children, padded = true, className = '' }) => {
  const base = padded ? 'p-6 md:p-7' : '';
  const classes = [base, className].filter(Boolean).join(' ');
  return <div className={classes}>{children}</div>;
};

const CTAButton: FC<{ href: string; label: string }> = ({ href, label }) => (
  <a
    href={href}
    className="inline-flex w-fit items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-white dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800"
  >
    {label}
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M7 17L17 7" />
      <path d="M7 7h10v10" />
    </svg>
  </a>
);

type InfoCardProps = {
  eyebrow?: string;
  title: string;
  summary?: string;
  description: string;
  ctaHref: string;
  ctaLabel: string;
  badges?: string[];
  className?: string;
};

const InfoCard: FC<InfoCardProps> = ({ eyebrow, title, summary, description, ctaHref, ctaLabel, badges = [], className = '' }) => {
  const hasBadges = badges.length > 0;
  const leftColumnClass = [
    'flex flex-col gap-4 md:flex-1 md:h-full',
    hasBadges ? 'md:justify-between md:pr-6' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Card className={className}>
      <CardBody className="flex h-full flex-col gap-6">
        <div className="flex flex-col gap-2">
          {eyebrow && (
            <span className="text-xs font-medium uppercase tracking-[0.22em] text-zinc-200 dark:text-zinc-500">{eyebrow}</span>
          )}
          <h4 className="font-creato text-xl font-light text-zinc-50 dark:text-zinc-900 md:text-2xl">{title}</h4>
          {summary && <p className="text-sm text-zinc-300 dark:text-zinc-600 md:text-base">{summary}</p>}
        </div>
        <div className={`flex flex-1 flex-col gap-6 pt-6 ${hasBadges ? 'md:flex-row md:items-stretch md:gap-6 md:pt-6' : ''}`}>
          <div className={leftColumnClass}>
            <p className="text-sm text-zinc-200 dark:text-zinc-600 md:text-base md:leading-relaxed">{description}</p>
            <div className="mt-4 md:mt-auto">
              <CTAButton href={ctaHref} label={ctaLabel} />
            </div>
          </div>
          {hasBadges && (
            <div className="mt-6 flex flex-col gap-2 md:mt-0 md:min-w-[150px] md:shrink-0 md:border-l md:border-white/20 md:pl-6 dark:md:border-zinc-300/60">
              {badges.map((badge) => (
                <span
                  key={badge}
                  className="rounded-full border border-white/20 bg-white/5 px-2.5 py-1 text-[9px] font-medium uppercase tracking-[0.18em] text-zinc-200 dark:border-zinc-300/60 dark:bg-zinc-200/30 dark:text-zinc-800"
                >
                  {badge}
                </span>
              ))}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

const ServicesShowcaseSection: FC = () => {
  return (
    <section className="py-10 md:py-16">
      <div className="rounded-3xl bg-zinc-900 ring-1 ring-black/10 p-5 text-white shadow-[0_20px_60px_-40px_rgba(0,0,0,0.7)] dark:bg-zinc-100 dark:ring-zinc-300 dark:text-zinc-900 md:p-8">
        {/* Header */}
        <div className="mb-6 grid grid-cols-1 gap-6 md:mb-8 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <h3 className="text-[clamp(26px,3vw,40px)] leading-[1.1] text-white dark:text-zinc-900">
              Build the future faster with an AI-native design and engineering partner.
            </h3>
          </div>
          <div className="text-sm text-white/80 md:text-base md:pl-12 lg:pl-20 dark:text-zinc-600">
            <p>Growth teams, founders, and operators lean on us when timelines are tight and the next release has to land. We mix code, no-code, and AI to unlock outcomes and leave you with the playbooks to keep scaling.</p>
          </div>
        </div>

        {/* Spotlight columns */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <div className="grid gap-6 md:grid-rows-[auto_1fr]">
            <InfoCard
              title="Make your team 10× faster"
              description="AI-first execution that ships value weekly so the metrics you care about move. We embed with your team to clear blockers and keep the roadmap breathing."
              ctaHref="/services#velocity"
              ctaLabel="Velocity engineering"
              badges={['Launch', 'Design', 'Weekly', 'Metric']}
              className="h-full"
            />
            <Card className="h-full overflow-hidden rounded-3xl">
              <img
                src="/hero-images/hero-2.jpg"
                alt="Product preview"
                className="h-full w-full object-cover"
              />
            </Card>
          </div>
          <div className="grid gap-6 md:grid-rows-[1fr_auto]">
            <Card className="h-full overflow-hidden rounded-3xl">
              <img
                src="/hero-images/hero-3.jpg"
                alt="Interface example"
                className="h-full w-full object-cover"
              />
            </Card>
            <InfoCard
              title="Apps built in weeks, not months"
              description="Production-ready front-ends with enforceable budgets, polish, and observability. We own the layer end-to-end and hand it back with monitoring and documentation."
              ctaHref="/services#web"
              ctaLabel="Web development"
              badges={['React', 'Typed', 'Stacks', 'Polish']}
              className="h-full"
            />
          </div>
        </div>

        {/* Row 3: small text cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <InfoCard
            title="AI agent development"
            description="Agents with retrieval, tool orchestration, and guardrails so they deliver value without drama. You get visibility, alerts, and human-in-the-loop overrides."
            ctaHref="/services#ai"
            ctaLabel="AI systems"
          />
          <InfoCard
            title="Sales prototyping"
            description="High-fidelity flows and clickable stories that make your solution impossible to ignore. We script demos around objections so your team can close faster."
            ctaHref="/services#prototypes"
            ctaLabel="Prototyping"
          />
          <InfoCard
            title="Process automation"
            description="Layer automations across teams, integrate systems, and keep humans in the loop. Every flow ships with oversight dashboards and training for operators."
            ctaHref="/services#automation"
            ctaLabel="Automation"
          />
        </div>

        {/* Row 4: small text cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <InfoCard
            title="Branding + strategy"
            description="Brand systems that scale across product, marketing, and comms with guardrails teams actually use. We deliver componentized assets so every surface stays on voice."
            ctaHref="/services#design"
            ctaLabel="Branding"
          />
          <InfoCard
            title="Slide deck templating"
            description="Reusable slide systems that speed up fundraising, sales, and ops presentations. Templates come with story beats, icon sets, and motion guidance."
            ctaHref="/services#decks"
            ctaLabel="Deck systems"
          />
          <InfoCard
            title="Product strategy"
            description="Define positioning, roadmap bets, and metrics so teams focus on the moves that matter. We co-create GTM plays and measurement plans you can run immediately."
            ctaHref="/services#product"
            ctaLabel="Product strategy"
          />
        </div>
      </div>
    </section>
  );
};

export default ServicesShowcaseSection;
